/**
 * EPBCS Phase 4 — Workforce Budgeting.
 * Tabs: Existing Employees, New Hires, Pay Scales, Review.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash, FloppyDisk, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ActionButton } from "./WorkflowActions";
import { Field, Input, Select, Btn } from "./common";
import { PovBar, useDims, fmtMonth, fmtNum } from "./povUtils";
import { useCapexPov, capexPovFields } from "./CapexSection";

const CATEGORIES = ["Permanent", "Temporary", "Contract"];

export default function WorkforceSection({ sub }) {
  const tab = ["increment", "attrition", "existing", "hires", "scales", "review"].includes(sub) ? sub : "existing";
  return (
    <div data-testid="epbcs-workforce-section">
      {tab === "increment" && <IncrementTab />}
      {tab === "attrition" && <AttritionTab />}
      {tab === "existing" && <ExistingTab />}
      {tab === "hires" && <HiresTab />}
      {tab === "scales" && <ScalesTab />}
      {tab === "review" && <WfReviewTab />}
    </div>
  );
}

const useWfData = (endpoint, extraBody) => {
  const dims = useDims();
  const [pov, setPov] = useCapexPov(dims);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => { api.get("/epbcs/my-access").then((r) => setIsAdmin(r.data.role === "admin")); }, []);
  const load = () => {
    if (!pov) return;
    setError(null);
    api.post(endpoint, { pov, ...(extraBody || {}) })
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); setError(e?.response?.data?.detail || e.message); });
  };
  useEffect(load, [pov]); // eslint-disable-line
  return { dims, pov, setPov, data, error, load, isAdmin };
};

// ------------------------------------------------- Increment assumptions ---
const IncrementTab = () => {
  const { dims, pov, setPov, data, error, load } = useWfData("/epbcs/employees/list", { form: "wf_increment" });
  const [edited, setEdited] = useState({});
  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid="increment-error">{error}</p>;
  const canEdit = data && (data.perm === "input" || data.perm === "full");
  const row = (e) => edited[e.id] || { id: e.id, increment_pct: e.increment_pct ?? "",
    effective_from: e.increment_effective_from || "", comments: e.increment_comments || "" };
  const setRow = (e, patch) => setEdited({ ...edited, [e.id]: { ...row(e), ...patch } });

  const save = async (e) => {
    try {
      await api.post("/epbcs/employees/increment", row(e));
      toast.success(`Increment saved for ${e.employee_code}`);
      setEdited((prev) => { const n = { ...prev }; delete n[e.id]; return n; });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save increment");
    }
  };

  return (
    <div className="space-y-4" data-testid="wf-increment-tab">
      <PovBar testid="pov-wf_increment" pov={pov} setPov={setPov} fields={capexPovFields(dims)} />
      <p className="text-[11px] text-slate-500">
        Increment % is applied to the employee's Current CTC (Monthly Total) from the Effective From month
        onward — Revised CTC = Current CTC × (1 + Increment %). Reflected in Existing Employee costs and Review.
      </p>
      {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <table className="text-[12px] w-full">
            <thead>
              <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="py-2 px-3">Code</th><th className="py-2 px-2 min-w-[130px]">Name</th>
                <th className="py-2 px-2">Position</th><th className="py-2 px-2">Grade</th>
                <th className="py-2 px-2 text-right">Current CTC / mo</th>
                <th className="py-2 px-2 text-right">Increment %</th>
                <th className="py-2 px-2">Effective From</th>
                <th className="py-2 px-2 min-w-[140px]">Comments</th>
                <th className="py-2 px-2 text-right">Revised CTC / mo</th>
                {canEdit && <th className="w-8" />}
              </tr>
            </thead>
            <tbody>
              {data.employees.map((e) => {
                const r = row(e);
                const dirty = !!edited[e.id];
                const revised = r.increment_pct ? e.monthly_total * (1 + (+r.increment_pct || 0) / 100) : e.monthly_total;
                return (
                  <tr key={e.id} className="border-b border-slate-100" data-testid={`inc-row-${e.employee_code}`}>
                    <td className="py-1.5 px-3 font-mono font-semibold">{e.employee_code}</td>
                    <td className="py-1.5 px-2 whitespace-nowrap">{e.employee_name}</td>
                    <td className="py-1.5 px-2">{e.position}</td>
                    <td className="py-1.5 px-2">{e.grade}</td>
                    <td className="py-1.5 px-2 text-right">{fmtNum(e.monthly_total)}</td>
                    <td className="py-1 px-1">
                      <input type="number" min="0" step="0.1" value={r.increment_pct} disabled={!canEdit}
                        data-testid={`inc-pct-${e.employee_code}`}
                        onChange={(ev) => setRow(e, { increment_pct: ev.target.value })}
                        className="w-16 border border-slate-200 px-1 py-0.5 text-[11px] text-right rounded" />
                    </td>
                    <td className="py-1 px-1">
                      <input type="date" value={r.effective_from} disabled={!canEdit}
                        data-testid={`inc-effective-${e.employee_code}`}
                        onChange={(ev) => setRow(e, { effective_from: ev.target.value })}
                        className="border border-slate-200 px-1 py-0.5 text-[11px] rounded" />
                    </td>
                    <td className="py-1 px-1">
                      <input type="text" value={r.comments} disabled={!canEdit}
                        data-testid={`inc-comments-${e.employee_code}`}
                        onChange={(ev) => setRow(e, { comments: ev.target.value })}
                        className="w-full border border-slate-200 px-1.5 py-0.5 text-[11px] rounded" />
                    </td>
                    <td className="py-1.5 px-2 text-right font-semibold text-[#0B1E3F]">{fmtNum(revised)}</td>
                    {canEdit && (
                      <td className="py-1.5 px-2">
                        <button onClick={() => save(e)} disabled={!dirty} data-testid={`inc-save-${e.employee_code}`}
                          className={dirty ? "text-[#0B1E3F]" : "text-slate-300"}><FloppyDisk size={15} weight="bold" /></button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {data.employees.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-slate-400">No employees for this POV yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// -------------------------------------------- Attrition / notice period ----
const AttritionTab = () => {
  const { dims, pov, setPov, data, error, load } = useWfData("/epbcs/employees/list", { form: "wf_attrition" });
  const [edited, setEdited] = useState({});
  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid="attrition-error">{error}</p>;
  const canEdit = data && (data.perm === "input" || data.perm === "full");
  const row = (e) => edited[e.id] || { id: e.id, serving_notice: !!e.serving_notice,
    last_working_date: e.last_working_date || "", comments: e.attrition_comments || "" };
  const setRow = (e, patch) => setEdited({ ...edited, [e.id]: { ...row(e), ...patch } });

  const save = async (e) => {
    const r = row(e);
    if (r.serving_notice && !r.last_working_date) {
      toast.error("Expected Last Working Date is required when Serving Notice is Yes");
      return;
    }
    try {
      await api.post("/epbcs/employees/attrition", r);
      toast.success(`Attrition status saved for ${e.employee_code}`);
      setEdited((prev) => { const n = { ...prev }; delete n[e.id]; return n; });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save attrition status");
    }
  };

  return (
    <div className="space-y-4" data-testid="wf-attrition-tab">
      <PovBar testid="pov-wf_attrition" pov={pov} setPov={setPov} fields={capexPovFields(dims)} />
      <p className="text-[11px] text-slate-500">
        Employees serving notice drop out of Existing Employee costs from the month after their Expected Last Working Date.
      </p>
      {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <table className="text-[12px] w-full">
            <thead>
              <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="py-2 px-3">Code</th><th className="py-2 px-2 min-w-[130px]">Name</th>
                <th className="py-2 px-2">Position</th><th className="py-2 px-2">Grade</th>
                <th className="py-2 px-2 text-right">Current CTC / mo</th>
                <th className="py-2 px-2">Serving Notice?</th>
                <th className="py-2 px-2">Expected Last Working Date</th>
                <th className="py-2 px-2 min-w-[140px]">Comments</th>
                {canEdit && <th className="w-8" />}
              </tr>
            </thead>
            <tbody>
              {data.employees.map((e) => {
                const r = row(e);
                const dirty = !!edited[e.id];
                return (
                  <tr key={e.id} className="border-b border-slate-100" data-testid={`att-row-${e.employee_code}`}>
                    <td className="py-1.5 px-3 font-mono font-semibold">{e.employee_code}</td>
                    <td className="py-1.5 px-2 whitespace-nowrap">{e.employee_name}</td>
                    <td className="py-1.5 px-2">{e.position}</td>
                    <td className="py-1.5 px-2">{e.grade}</td>
                    <td className="py-1.5 px-2 text-right">{fmtNum(e.monthly_total)}</td>
                    <td className="py-1 px-1">
                      <Select value={r.serving_notice ? "Yes" : "No"} disabled={!canEdit}
                        data-testid={`att-serving-${e.employee_code}`}
                        onChange={(ev) => setRow(e, { serving_notice: ev.target.value === "Yes",
                          last_working_date: ev.target.value === "Yes" ? r.last_working_date : "" })}
                        className="w-24">
                        <option value="No">No</option><option value="Yes">Yes</option>
                      </Select>
                    </td>
                    <td className="py-1 px-1">
                      <input type="date" value={r.last_working_date} disabled={!canEdit || !r.serving_notice}
                        data-testid={`att-lwd-${e.employee_code}`}
                        onChange={(ev) => setRow(e, { last_working_date: ev.target.value })}
                        className={`border border-slate-200 px-1 py-0.5 text-[11px] rounded ${!r.serving_notice ? "bg-slate-100 text-slate-400" : ""}`} />
                    </td>
                    <td className="py-1 px-1">
                      <input type="text" value={r.comments} disabled={!canEdit}
                        data-testid={`att-comments-${e.employee_code}`}
                        onChange={(ev) => setRow(e, { comments: ev.target.value })}
                        className="w-full border border-slate-200 px-1.5 py-0.5 text-[11px] rounded" />
                    </td>
                    {canEdit && (
                      <td className="py-1.5 px-2">
                        <button onClick={() => save(e)} disabled={!dirty} data-testid={`att-save-${e.employee_code}`}
                          className={dirty ? "text-[#0B1E3F]" : "text-slate-300"}><FloppyDisk size={15} weight="bold" /></button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {data.employees.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-slate-400">No employees for this POV yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------- Existing employees -----
const ExistingTab = () => {
  const { dims, pov, setPov, data, error, load, isAdmin } = useWfData("/epbcs/employees/list");
  const [draft, setDraft] = useState(null);
  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid="existing-error">{error}</p>;
  const canEdit = data && (data.perm === "input" || data.perm === "full");

  const save = async (e) => {
    try {
      await api.post("/epbcs/employees", { pov, ...e });
      toast.success(e.id ? "Employee updated" : "Employee added");
      setDraft(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save employee");
    }
  };
  const seed = async () => { await api.post("/epbcs/workforce/seed-demo", { pov }); load(); };

  return (
    <div className="space-y-4" data-testid="wf-existing-tab">
      <PovBar testid="pov-wf_existing" pov={pov} setPov={setPov} fields={capexPovFields(dims)}
        extra={<ActionButton section="workforce" pov={pov} onChanged={load} />} />
      <div className="flex gap-2">
        {canEdit && !draft && (
          <button onClick={() => setDraft({ division: dims.division?.[0]?.code || "",
            employee_code: "", employee_name: "", position: dims.position?.[0]?.code || "",
            grade: dims.grade?.[0]?.code || "", employee_category: "Permanent", monthly: {} })}
            data-testid="new-employee-btn"
            className="inline-flex items-center gap-1.5 bg-[#0B1E3F] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded">
            <Plus size={13} weight="bold" /> Add employee
          </button>
        )}
        {isAdmin && data && data.employees.length === 0 && (
          <button onClick={seed} data-testid="seed-workforce-btn"
            className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100">
            Seed demo workforce (pay scales + 5 employees)
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-500">
        "Forecast / Budget Total" is calculated after applying each employee's Increment % (from the
        Increment tab) and Notice Period cut-off (from the Attrition tab) — CY Forecast total on top,
        Budget Year total below.
      </p>
      {draft && data && (
        <div className="rounded-sm border border-[#0B1E3F]/30 bg-slate-50 p-4" data-testid="employee-draft">
          <h4 className="font-bold text-sm mb-3">{draft.id ? "Edit employee" : "Add employee"}</h4>          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label={draft.id ? "Employee Code * (locked)" : "Employee Code *"}>
              <Input data-testid="emp-code" value={draft.employee_code} disabled={!!draft.id}
                className={draft.id ? "bg-slate-100 text-slate-500 cursor-not-allowed" : ""}
                onChange={(e) => setDraft({ ...draft, employee_code: e.target.value })} /></Field>
            <Field label="Name *"><Input data-testid="emp-name" value={draft.employee_name}
              onChange={(e) => setDraft({ ...draft, employee_name: e.target.value })} /></Field>
            <Field label="Division *">
              <Select value={draft.division} onChange={(e) => setDraft({ ...draft, division: e.target.value })}>
                {(dims.division || []).map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </Select>
            </Field>
            <Field label="Position *">
              <Select value={draft.position} onChange={(e) => setDraft({ ...draft, position: e.target.value })}>
                {(dims.position || []).map((p) => <option key={p.code} value={p.code}>{p.code} — {p.name}</option>)}
              </Select>
            </Field>
            <Field label="Grade *">
              <Select value={draft.grade} onChange={(e) => setDraft({ ...draft, grade: e.target.value })}>
                {(dims.grade || []).map((g) => <option key={g.code} value={g.code}>{g.code} — {g.name}</option>)}
              </Select>
            </Field>
            <Field label="Category *">
              <Select value={draft.employee_category} onChange={(e) => setDraft({ ...draft, employee_category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </Select>
            </Field>
            {data.salary_accounts.map((a) => (
              <Field key={a.code} label={/basic/i.test(a.name) ? `${a.name} / mo *` : `${a.name} / mo`}>
                <Input type="number" min="0" data-testid={`emp-monthly-${a.code}`} value={draft.monthly[a.code] ?? ""}
                  onChange={(e) => setDraft({ ...draft, monthly: { ...draft.monthly, [a.code]: Math.max(0, +e.target.value) || 0 } })} />
              </Field>
            ))}
          </div>
          <div className="mt-3 flex gap-2 justify-end">
            <button onClick={() => setDraft(null)} className="border border-slate-300 text-slate-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded">Cancel</button>
            <Btn onClick={() => save(draft)}
              disabled={!draft.employee_code ||
                data.salary_accounts.some((a) => /basic/i.test(a.name) && !(+draft.monthly[a.code] > 0))}
              data-testid="emp-save">Save employee</Btn>
          </div>
        </div>
      )}
      {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <table className="text-[12px] w-full">
            <thead>
              <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="py-2 px-3">Code</th><th className="py-2 px-2 min-w-[130px]">Name</th>
                <th className="py-2 px-2">Division</th><th className="py-2 px-2">Position</th>
                <th className="py-2 px-2">Grade</th><th className="py-2 px-2">Category</th>
                {data.salary_accounts.map((a) => <th key={a.code} className="py-2 px-2 text-right min-w-[90px]">{a.name}</th>)}
                <th className="py-2 px-2 text-right">Monthly Total</th>
                <th className="py-2 px-2 text-right min-w-[110px]" data-testid="col-forecast-budget-total">Forecast / Budget Total</th>
                {canEdit && <th className="w-8" />}
              </tr>
            </thead>
            <tbody>
              {data.employees.map((e) => (
                <tr key={e.id} className="border-b border-slate-100" data-testid={`emp-row-${e.employee_code}`}>
                  <td className="py-1.5 px-3 font-mono font-semibold">{e.employee_code}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap">{e.employee_name}</td>
                  <td className="py-1.5 px-2">{e.division}</td>
                  <td className="py-1.5 px-2">{e.position}</td>
                  <td className="py-1.5 px-2">{e.grade}</td>
                  <td className="py-1.5 px-2">{e.employee_category}</td>
                  {data.salary_accounts.map((a) => (
                    <td key={a.code} className="py-1.5 px-2 text-right">{fmtNum(e.monthly?.[a.code] || 0)}</td>
                  ))}
                  <td className="py-1.5 px-2 text-right font-bold" data-testid={`emp-total-${e.employee_code}`}>{fmtNum(e.monthly_total)}</td>
                  <td className="py-1.5 px-2 text-right" data-testid={`emp-forecast-budget-${e.employee_code}`}>
                    <div className="leading-tight">
                      <div className="text-[#0B1E3F] font-semibold">FC {fmtNum(e.forecast_total)}</div>
                      <div className="text-slate-500">BUD {fmtNum(e.budget_total)}</div>
                    </div>
                  </td>
                  {canEdit && (
                    <td className="py-1.5 px-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setDraft({ id: e.id, division: e.division,
                            employee_code: e.employee_code, employee_name: e.employee_name,
                            position: e.position, grade: e.grade,
                            employee_category: e.employee_category, monthly: { ...(e.monthly || {}) } })}
                          data-testid={`emp-edit-${e.employee_code}`}
                          className="text-slate-400 hover:text-[#0B1E3F]"><PencilSimple size={13} /></button>
                        <button onClick={async () => { await api.delete(`/epbcs/employees/${e.id}`); load(); }}
                          data-testid={`emp-delete-${e.employee_code}`}
                          className="text-slate-400 hover:text-red-600"><Trash size={13} /></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {data.employees.length === 0 && (
                <tr><td colSpan={9 + data.salary_accounts.length} className="py-6 text-center text-slate-400">
                  No employees for this POV yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ----------------------------------------------------------- New hires -----
const HiresTab = () => {
  const { dims, pov, setPov, data, error, load } = useWfData("/epbcs/new-hires/list");
  const [draft, setDraft] = useState(null);
  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid="hires-error">{error}</p>;
  const canEdit = data && (data.perm === "input" || data.perm === "full");

  const save = async () => {
    try {
      await api.post("/epbcs/new-hires", { pov, ...draft, headcounts: +draft.headcounts || 0 });
      toast.success("Hire request saved");
      setDraft(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save hire request");
    }
  };

  return (
    <div className="space-y-4" data-testid="wf-hires-tab">
      <PovBar testid="pov-wf_hires" pov={pov} setPov={setPov} fields={capexPovFields(dims)} />
      <p className="text-[11px] text-slate-500">
        Compensation is auto-priced from the Grade pay scale × headcount from the start month.
      </p>
      {canEdit && !draft && (
        <button onClick={() => setDraft({ grade: dims.grade?.[0]?.code || "",
          start_date: "", nationality_type: "", employee_category: "Permanent", headcounts: 1 })}
          data-testid="new-hire-btn"
          className="inline-flex items-center gap-1.5 bg-[#0B1E3F] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded">
          <Plus size={13} weight="bold" /> New request
        </button>
      )}
      {draft && (
        <div className="rounded-sm border border-[#0B1E3F]/30 bg-slate-50 p-4" data-testid="hire-draft">
          <h4 className="font-bold text-sm mb-3">New hire request <span className="text-[11px] font-normal text-slate-500">(Request ID auto-generated: TBH_FYyy_Seq)</span></h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Grade *">
              <Select value={draft.grade} data-testid="hire-grade" onChange={(e) => setDraft({ ...draft, grade: e.target.value })}>
                {(dims.grade || []).map((g) => <option key={g.code} value={g.code}>{g.code} — {g.name}</option>)}
              </Select>
            </Field>
            <Field label="Start Date *"><Input type="date" data-testid="hire-start" value={draft.start_date}
              onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} /></Field>
            <Field label="Nationality Type *"><Input data-testid="hire-nationality" value={draft.nationality_type}
              placeholder="e.g. National, Expat" onChange={(e) => setDraft({ ...draft, nationality_type: e.target.value })} /></Field>
            <Field label="Employee Category *">
              <Select value={draft.employee_category} data-testid="hire-category" onChange={(e) => setDraft({ ...draft, employee_category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Headcounts * (whole number)"><Input type="number" min="1" step="1" data-testid="hire-headcounts" value={draft.headcounts}
              onChange={(e) => setDraft({ ...draft, headcounts: e.target.value })} /></Field>
          </div>
          <div className="mt-3 flex gap-2 justify-end">
            <button onClick={() => setDraft(null)} className="border border-slate-300 text-slate-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded">Cancel</button>
            <Btn onClick={save} disabled={!draft.start_date || !draft.grade || !draft.nationality_type ||
              !(+draft.headcounts > 0) || +draft.headcounts !== Math.floor(+draft.headcounts)} data-testid="hire-save">Save request</Btn>
          </div>
        </div>
      )}
      {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <table className="text-[12px] w-full">
            <thead>
              <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="py-2 px-3">Request ID</th>
                <th className="py-2 px-2">Grade</th><th className="py-2 px-2">Start</th>
                <th className="py-2 px-2">Nationality</th><th className="py-2 px-2">Category</th>
                <th className="py-2 px-2 text-right">Headcounts</th>
                {data.salary_accounts.map((a) => <th key={a.code} className="py-2 px-2 text-right min-w-[90px]">{a.name}</th>)}
                <th className="py-2 px-2 text-right">Monthly Total</th>
                {canEdit && <th className="w-8" />}
              </tr>
            </thead>
            <tbody>
              {data.hires.map((h) => (
                <tr key={h.id} className="border-b border-slate-100" data-testid={`hire-row-${h.id}`}>
                  <td className="py-1.5 px-3 font-mono">{h.request_id}</td>
                  <td className="py-1.5 px-2">{h.grade}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap">{h.start_date}</td>
                  <td className="py-1.5 px-2">{h.nationality_type}</td>
                  <td className="py-1.5 px-2">{h.employee_category}</td>
                  <td className="py-1.5 px-2 text-right font-semibold">{fmtNum(h.headcounts)}</td>
                  {data.salary_accounts.map((a) => (
                    <td key={a.code} className="py-1.5 px-2 text-right text-[#0B1E3F]">{fmtNum(h.compensation?.[a.code] || 0)}</td>
                  ))}
                  <td className="py-1.5 px-2 text-right font-bold" data-testid={`hire-total-${h.id}`}>{fmtNum(h.monthly_total)}</td>
                  {canEdit && (
                    <td className="py-1.5 px-2">
                      <button onClick={async () => { await api.delete(`/epbcs/new-hires/${h.id}`); load(); }}
                        data-testid={`hire-delete-${h.id}`}
                        className="text-slate-400 hover:text-red-600"><Trash size={13} /></button>
                    </td>
                  )}
                </tr>
              ))}
              {data.hires.length === 0 && (
                <tr><td colSpan={8 + data.salary_accounts.length} className="py-6 text-center text-slate-400">
                  No hire requests for this POV yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ----------------------------------------------------------- Pay scales ----
const ScalesTab = () => {
  const dims = useDims();
  const [pov, setPov] = useCapexPov(dims);
  const [year, setYear] = useState("");
  const [scales, setScales] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState(null);
  const [edited, setEdited] = useState({});

  useEffect(() => {
    api.get("/epbcs/settings").then((r) => setYear((r.data.budget_start || "2027-01").slice(0, 4)));
    api.get("/epbcs/dimensions?dim_type=account").then((r) =>
      setAccounts(r.data.filter((a) => a.attrs?.budget_method === "workforce")));
  }, []);
  const load = () => {
    if (!pov || !year) return;
    setError(null);
    const qs = new URLSearchParams({ ...pov, year }).toString();
    api.get(`/epbcs/pay-scales?${qs}`)
      .then((r) => { setScales(r.data); setEdited({}); })
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  };
  useEffect(load, [pov, year]); // eslint-disable-line

  if (error) return <p className="text-sm text-red-600" data-testid="scales-error">{error}</p>;
  if (!dims || !pov || !scales) return <p className="text-sm text-slate-400">Loading…</p>;

  const codes = (dims.grade || []).map((d) => d.code);
  const key = (c, cat) => `${c}|${cat}`;
  const getRates = (c, cat) => edited[key(c, cat)] ||
    scales.find((s) => s.code === c && s.employee_category === cat)?.rates || {};

  const saveCell = async (c, cat) => {
    try {
      await api.post("/epbcs/pay-scales", { pov: { ...pov, year }, code: c,
        employee_category: cat, rates: getRates(c, cat) });
      toast.success(`Pay scale saved for ${c} / ${cat}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save pay scale");
    }
  };

  return (
    <div className="space-y-4" data-testid="wf-scales-tab">
      <div className="flex flex-wrap items-end gap-3">
        <PovBar testid="pov-wf_scales" pov={pov} setPov={setPov} fields={capexPovFields(dims)} />
        <Field label="Year *"><Input className="w-24" data-testid="scales-year" value={year}
          onChange={(e) => setYear(e.target.value)} /></Field>
      </div>
      <div className="rounded-sm border border-slate-200 bg-white shadow-sm p-5">
        <table className="text-[12px] w-full">
          <thead>
            <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">Grade</th>
              <th className="py-2 pr-3">Category</th>
              {accounts.map((a) => <th key={a.code} className="py-2 px-2 text-right min-w-[110px]">{a.name} / mo</th>)}
              <th className="w-16" />
            </tr>
          </thead>
          <tbody>
            {codes.flatMap((c) => CATEGORIES.map((cat) => {
              const rates = getRates(c, cat);
              const dirty = !!edited[key(c, cat)];
              return (
                <tr key={key(c, cat)} className="border-b border-slate-100" data-testid={`scale-row-${c}-${cat}`}>
                  <td className="py-1.5 pr-3 font-mono font-semibold">{c}</td>
                  <td className="py-1.5 pr-3">{cat}</td>
                  {accounts.map((a) => (
                    <td key={a.code} className="py-1 px-1 text-right">
                      <input type="number" value={rates[a.code] ?? ""} data-testid={`scale-${c}-${cat}-${a.code}`}
                        onChange={(e) => setEdited({ ...edited, [key(c, cat)]: { ...rates, [a.code]: +e.target.value || 0 } })}
                        className="w-24 border border-slate-200 px-1 py-0.5 text-[11px] text-right rounded" />
                    </td>
                  ))}
                  <td className="py-1.5 px-2">
                    <button onClick={() => saveCell(c, cat)} disabled={!dirty} data-testid={`scale-save-${c}-${cat}`}
                      className={dirty ? "text-[#0B1E3F]" : "text-slate-300"}><FloppyDisk size={15} weight="bold" /></button>
                  </td>
                </tr>
              );
            }))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// -------------------------------------------------------------- Review -----
const WfReviewTab = () => {
  const { dims, pov, setPov, data, error } = useWfData("/epbcs/workforce-review");
  if (!dims || !pov) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid="wf-review-error">{error}</p>;
  return (
    <div className="space-y-4" data-testid="wf-review-tab">
      <PovBar testid="pov-wf_review" pov={pov} setPov={setPov} fields={capexPovFields(dims)} />
      {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <table className="text-[12px] w-full">
            <thead>
              <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="py-2 px-3 min-w-[150px]">Salary Account</th><th className="py-2 px-2">Measure</th>
                <th className="py-2 px-2 text-right">Total</th>
                {data.months.map((m) => <th key={m} className="py-2 px-2 text-right min-w-[70px] whitespace-nowrap">{fmtMonth(m)}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.rows.flatMap((r) => [["existing", "Existing"], ["new_hires", "New Hires"], ["total", "Total"]].map(([k, l]) => (
                <tr key={`${r.account}-${k}`} className={`border-b border-slate-100 ${k === "total" ? "bg-slate-50 font-bold" : ""}`}
                  data-testid={`wf-review-${r.account}-${k}`}>
                  <td className="py-1.5 px-3">{k === "existing" ? `${r.account} — ${r.name}` : ""}</td>
                  <td className={`py-1.5 px-2 text-[10px] font-bold uppercase ${k === "new_hires" ? "text-[#0B1E3F]" : k === "total" ? "" : "text-slate-500"}`}>{l}</td>
                  <td className="py-1.5 px-2 text-right font-semibold">
                    {fmtNum(Object.values(r[k] || {}).reduce((s, v) => s + v, 0))}
                  </td>
                  {data.months.map((m) => (
                    <td key={m} className={`py-1.5 px-2 text-right whitespace-nowrap ${r[k]?.[m] ? "" : "text-slate-300"}`}>
                      {fmtNum(r[k]?.[m] || 0)}
                    </td>
                  ))}
                </tr>
              )))}
              {data.rows.length === 0 && (
                <tr><td colSpan={3 + data.months.length} className="py-6 text-center text-slate-400">
                  No workforce salary accounts configured (Budget Method = workforce).
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
